import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { useDateiUrl } from '../hooks/useDateiUrl';
import '../screens/Vorgang/Unterschriften.css';
import { Unterzeichner } from '../shared/constants';
import { formatDDMMYYYY } from '../shared/dateTime';
import { Vorgang } from '../types';
import { Signature } from './common/Signature';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { MEHRWERTSTEUER } from '../shared/domain/vorgang';
import { anredeAuswahl } from '../frontendConstants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: boolean) => void;
  readonly checkboxState: boolean;
};

export function Nachbesichtigung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState(event.target.checked)}
              name="nachbesichtigung"
              color="primary"
              data-testid="nachbesichtigungCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="nachbesichtigungLink"
              >
                Nachbesichtigung
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <NachbesichtigungModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function NachbesichtigungModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const nachbesichtigungUrl = useDateiUrl(vorgang?.unterschriften?.nachbesichtigung?.key);

  const signDate = vorgang?.unterschriften?.nachbesichtigung?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Auftrag zur Nachbesichtigung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="nachbesichtigungModal">
          <Grid size={12}>
            <Typography variant="h6">Auftrag zur Nachbesichtigung</Typography>
          </Grid>
          <Grid size={4}>
            Schadensereignis vom
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7}>
            {formatDDMMYYYY(vorgang.unfalldaten.datum)}
          </Grid>
          <Grid size={4}>
            Name des Auftraggebers
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7} data-testid="auftraggeberName">
            {anredeAuswahl.find((anrede) => anrede.value === vorgang.auftraggeber?.anrede)?.label} {vorgang.auftraggeber?.vorname}{' '}
            {vorgang.auftraggeber?.nachname}
          </Grid>
          <Grid size={4}>
            Anschrift des Auftraggebers
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7} data-testid="anschriftAuftraggeber">
            {vorgang.auftraggeber?.strasse} {vorgang.auftraggeber?.plz} {vorgang.auftraggeber?.ort}
          </Grid>
          <Grid size={4}>
            Kennzeichen des Auftraggebers
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7} data-testid="kennzeichenAuftraggeber">
            {vorgang.fahrzeugdaten.kennzeichen}
          </Grid>
          <Grid size={4}>
            Kennzeichen des Unfallgegners
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7} data-testid="kennzeichenUnfallgegner">
            {vorgang.unfalldaten.kennzeichen}
          </Grid>
          <Grid size={4}>
            Name des Auftragnehmers
          </Grid>
          <Grid size={1}>
            :
          </Grid>
          <Grid size={7}>
            Experts24 Tassone GmbH, Hildener Str. 28, 42697 Solingen
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§1 Geltung der Bedingungen für den Nachbesichtigungstermin</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Die Nachbesichtigung durch den Auftragnehmer (AN) für den Auftraggeber (AG) erfolgt ausschließlich aufgrund dieser Geschäftsbedingungen. Sie gilt
              nur für den Fall, dass nach einer Erstbesichtigung des verunfallten Fahrzeugs ein weiterer Besichtigungstermin zur genauen Schadensidentifikation
              erforderlich ist. Sie sind ergänzende Vereinbarung neben den Bedingungen zum Auftrag zur Gutachtenerstellung und den Bedingungen zur
              Fahrtkostenvereinbarung zur Gutachtenerstellung.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§2 Auftragserteilung</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Der Auftrag zur Nachbesichtigung erfolgt regelmäßig durch (auch digitale) Unterschrift des AG unter dem Auftragsformular und Zugang des
              unterschriebenen Auftragsformulars beim AN.
            </Typography>
            <Typography variant="body2">
              Der Auftrag kann alternativ auch im Wege der elektronischen Auftragserteilung zustande kommen. In diesem Fall gibt der AG ein verbindliches
              Angebot ab, indem er
            </Typography>
            <Typography variant="body2">
              - die im Beauftragungsprozess bereitgestellten Vertragsbedingungen durch Anklicken der hierfür vorgesehenen Check-Boxen akzeptiert und
            </Typography>
            <Typography variant="body2">
              - den Auftrag durch Betätigung des eindeutig beschrifteten Buttons „kostenpflichtig bestellen" bzw. „kostenpflichtig beauftragen" absendet.
            </Typography>
            <Typography variant="body2">
              Die Auftragsannahme erfolgt durch Erklärung des AN gegenüber dem AG, insbesondere durch elektronische Bestätigung der Auftragsannahme.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§3 Vergütung des Sachverständigen für die An- und Abfahrt zur Nachbesichtigung</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              (1) Der AN und der AG vereinbaren An- und Abfahrtskosten zur Nachbesichtigung in Höhe von 0,83 € netto (0,99 € brutto inkl. {MEHRWERTSTEUER}%
              gesetzlicher Mehrwertsteuer) pro gefahrenen Kilometer.
              <br />
              (2) Die An- und Abfahrtkosten zur Nachbesichtigung sind neben den An- und Abfahrtskosten zur Erstbesichtigung zur Gutachtenerstellung zu zahlen.
              <br />
              (3) Für An- und Abfahrt zur Nachbesichtigung berechnet der AN maximal 83,00 € netto (98,77 € brutto inkl. {MEHRWERTSTEUER}% gesetzlicher
              Mehrwertsteuer), sprich es werden maximal 100 km kostenmäßig berücksichtigt (Deckelung). Alle darüber hinausgehenden etwaigen Fahrtkosten für die
              Nachbesichtigung sind hiermit abgegolten.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§4 Stundenvergütung des Sachverständigen für die Nachbesichtigung</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Der AN und der AG vereinbaren, dass der AN für die Nachbesichtigung eine Vergütung von 100,00 netto (119,00 € brutto inkl. {MEHRWERTSTEUER}%
              gesetzlicher Mehrwertsteuer) pro Stunde erhält. Dabei wird minutengenau abgerechnet.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§5 Kostenerstattung für Fremdkosten durch den AG</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Werden im Rahmen der Nachbesichtigung Kosten für Hilfsleistungen von Dritten erhoben, wie:
              <ul>
                <li>Kosten für die Demontage der Stoßfänger</li>
                <li>Kosten für die Nutzung einer Hebebühne oder</li>
                <li>Kosten für eine Achsvermessung</li>
              </ul>
              werden diese Fremdkosten im Verhältnis zum Dritten zunächst vom AN getragen. Der AN und der AG vereinbaren, dass diese Kosten dem AN vom AG
              erstattet werden. Hierzu stellt der AN dem AG die durch ihn getragenen Fremdkosten in Rechnung.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={nachbesichtigungUrl}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.nachbesichtigung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
